export const formatBirthday = (dateStr: string | null | undefined, hasDeathDate: boolean = false, locale: 'ja' | 'en' | 'es' = 'ja'): string => {
    if (!dateStr) return '-';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        if (hasDeathDate) {
            if (locale === 'en') {
                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            }
            if (locale === 'es') {
                return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
            }
            return `${year}年${month}月${day}日`;
        }

        const today = new Date();
        let age = today.getFullYear() - year;
        const birthdayThisYear = new Date(today.getFullYear(), month - 1, day);
        if (today < birthdayThisYear) {
            age--;
        }

        if (locale === 'en') {
            const d = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            return `${d} (Age: ${age})`;
        }
        if (locale === 'es') {
            const d = date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
            return `${d} (${age} años)`;
        }
        return `${year}年${month}月${day}日（${age}歳）`;
    } catch {
        return dateStr;
    }
};

export const formatDeathDate = (deathDateStr: string | null | undefined, birthdayStr: string | null | undefined, locale: 'ja' | 'en' | 'es' = 'ja'): string => {
    if (!deathDateStr) return '';
    try {
        const deathDate = new Date(deathDateStr);
        if (isNaN(deathDate.getTime())) return deathDateStr;

        const year = deathDate.getFullYear();
        const month = deathDate.getMonth() + 1;
        const day = deathDate.getDate();

        if (birthdayStr) {
            const birthDate = new Date(birthdayStr);
            if (!isNaN(birthDate.getTime())) {
                let age = year - birthDate.getFullYear();
                const birthdayThisYear = new Date(year, birthDate.getMonth(), birthDate.getDate());
                if (deathDate < birthdayThisYear) {
                    age--;
                }
                if (locale === 'en') {
                    const d = deathDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    return `${d} (Aged ${age})`;
                }
                if (locale === 'es') {
                    const d = deathDate.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
                    return `${d} (fallecido a los ${age} años)`;
                }
                return `${year}年${month}月${day}日（${age}歳没）`;
            }
        }

        if (locale === 'en') {
            return deathDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
        if (locale === 'es') {
            return deathDate.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
        }
        return `${year}年${month}月${day}日`;
    } catch {
        return deathDateStr;
    }
};

export const formatPitchBat = (code: string | undefined, type: 'pitch' | 'bat', locale: 'ja' | 'en' | 'es') => {
    if (!code) return '';
    let c = code.toUpperCase();

    if (c.includes('右')) c = 'R';
    else if (c.includes('左')) c = 'L';
    else if (c.includes('両')) c = 'S';

    if (locale === 'ja') {
        if (type === 'pitch') {
            if (c === 'R') return '右投';
            if (c === 'L') return '左投';
            if (c === 'S') return '両投';
        } else {
            if (c === 'R') return '右打';
            if (c === 'L') return '左打';
            if (c === 'S') return '両打';
        }
    }
    if (locale === 'en') {
        if (c === 'R') return 'Right';
        if (c === 'L') return 'Left';
        if (c === 'S') return 'Switch';
    }
    if (locale === 'es') {
        if (c === 'R') return 'Derecha';
        if (c === 'L') return 'Izquierda';
        if (c === 'S') return 'Ambos';
    }
    return c;
};

export const formatDate = (dateStr: string | null | undefined, locale: 'ja' | 'en' | 'es' = 'ja'): string => {
    if (!dateStr) return '-';

    const normalized = dateStr.trim();
    const lowered = normalized.toLowerCase();
    const isRetiredLabel =
        /^引退/.test(normalized) ||
        lowered === 'retired' ||
        lowered === 'retired_unknown_date' ||
        lowered === 'retirado' ||
        lowered === 'retiro';

    if (isRetiredLabel) {
        if (locale === 'es') return 'OB';
        if (locale === 'en') return 'OB';
        return '引退';
    }

    const withLeagueLineBreaks = (text: string): string => {
        return text
            .replace(/([^\n])\s+((?:MLB|NPB)\s*\/)/g, '$1\n$2')
            .replace(/^\s+((?:MLB|NPB)\s*\/)/, '$1')
            .trim();
    };

    if (locale === 'ja') {
        return withLeagueLineBreaks(normalized);
    }

    const toLocale = locale === 'es' ? 'es-ES' : 'en-US';
    const monthStyle: 'long' | 'short' = locale === 'es' ? 'long' : 'short';

    const cleanNonJapaneseText = (text: string): string => {
        const hadLatin = /[A-Za-z]/.test(text);
        let cleaned = text
            .replace(/[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/g, ' ')
            .replace(/[、。・「」『』（）［］｛｝【】〈〉《》〜…　]/g, ' ')
            .replace(/[\u3000-\u303F\uFF00-\uFFEF]/g, ' ')
            .replace(/\s*\/\s*/g, ' / ')
            .replace(/\s+/g, ' ');

        if (!hadLatin) {
            cleaned = cleaned.replace(/\b\d+\b/g, ' ');
        }

        return cleaned.replace(/\s+/g, ' ').trim();
    };

    const formatYmd = (year: number, month: number, day: number): string | null => {
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString(toLocale, { year: 'numeric', month: monthStyle, day: 'numeric' });
    };

    const dateTokenRegex = /(\d{4})\s*[\u5E74]\s*(\d{1,2})\s*[\u6708]\s*(\d{1,2})\s*[\u65E5]|\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g;
    let match: RegExpExecArray | null;
    let lastIndex = 0;
    let hasDate = false;
    const parts: string[] = [];

    while ((match = dateTokenRegex.exec(normalized)) !== null) {
        const prefix = cleanNonJapaneseText(normalized.slice(lastIndex, match.index));
        if (prefix) parts.push(prefix);

        const year = Number(match[1] ?? match[4]);
        const month = Number(match[2] ?? match[5]);
        const day = Number(match[3] ?? match[6]);
        const formatted = formatYmd(year, month, day);
        if (formatted) {
            parts.push(formatted);
            hasDate = true;
        }

        lastIndex = dateTokenRegex.lastIndex;
    }

    if (hasDate) {
        const suffix = cleanNonJapaneseText(normalized.slice(lastIndex));
        if (suffix) parts.push(suffix);
        const merged = parts.join(' ').replace(/\s+/g, ' ').trim();
        return merged ? withLeagueLineBreaks(merged) : '-';
    }

    const parsed = new Date(normalized);
    if (!isNaN(parsed.getTime())) {
        const formatted = parsed.toLocaleDateString(toLocale, { year: 'numeric', month: monthStyle, day: 'numeric' });
        return withLeagueLineBreaks(formatted);
    }

    const cleaned = cleanNonJapaneseText(normalized);
    return cleaned ? withLeagueLineBreaks(cleaned) : '-';
};

export const formatYear = (yearStr: string | number | null | undefined, locale: 'ja' | 'en' | 'es' = 'ja'): string => {
    if (!yearStr) return '-';
    const s = String(yearStr);

    if (locale === 'ja') return s;

    if (s === '通算') return locale === 'es' ? 'Carrera' : 'Career';

    const match = s.match(/^(.*)通算(\d+)年$/);
    if (match) {
        const league = match[1];
        const years = match[2];
        if (locale === 'es') {
            return `Carrera ${league} (${years} años)`;
        }
        return `${league} Career (${years} yrs)`;
    }

    return s;
};

const isClose = (a: number, b: number, epsilon: number): boolean => Math.abs(a - b) <= epsilon;

export const formatInningsPitched = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const raw = typeof value === 'string' ? value.trim() : value;
    if (raw === '') return '-';

    const num = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(num)) return typeof raw === 'string' ? raw : String(raw);

    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const whole = Math.floor(abs);
    const fraction = abs - whole;

    // Already in baseball notation.
    if (isClose(fraction, 0, 1e-6)) return `${sign}${whole}`;
    if (isClose(fraction, 0.1, 1e-6)) return `${sign}${whole}.1`;
    if (isClose(fraction, 0.2, 1e-6)) return `${sign}${whole}.2`;

    // Convert decimal innings (e.g. 196.667) to baseball notation (196.2).
    const outs = Math.round(abs * 3);
    const reconstructed = outs / 3;
    if (Math.abs(abs - reconstructed) <= 0.02) {
        const innings = Math.floor(outs / 3);
        const remainder = outs % 3;
        if (remainder === 0) return `${sign}${innings}`;
        return `${sign}${innings}.${remainder}`;
    }

    return `${sign}${abs.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}`;
};

const POSITION_EN_MAP: Record<string, string> = {
    'P':  'Pitcher',
    'C':  'Catcher',
    '1B': 'First Baseman',
    '2B': 'Second Baseman',
    '3B': 'Third Baseman',
    'SS': 'Shortstop',
    'OF': 'Outfielder',
    'DH': 'Designated Hitter',
    'IF': 'Infielder',
};

const POSITION_ES_MAP: Record<string, string> = {
    'P':  'Lanzador',
    'C':  'Receptor',
    '1B': 'Primera Base',
    '2B': 'Segunda Base',
    '3B': 'Tercera Base',
    'SS': 'Campocorto',
    'OF': 'Jardinero',
    'DH': 'Bateador Designado',
    'IF': 'Jugador de Cuadro',
    // フルネームでの入力にも対応
    'Pitcher':          'Lanzador',
    'Catcher':          'Receptor',
    'First Baseman':    'Primera Base',
    'Second Baseman':   'Segunda Base',
    'Third Baseman':    'Tercera Base',
    'Shortstop':        'Campocorto',
    'Outfielder':       'Jardinero',
    'Left Fielder':     'Jardinero Izquierdo',
    'Center Fielder':   'Jardinero Central',
    'Right Fielder':    'Jardinero Derecho',
    'Designated Hitter':'Bateador Designado',
    'Infielder':        'Jugador de Cuadro',
    'Relief Pitcher':   'Lanzador Relevista',
    'Starting Pitcher': 'Lanzador Abridor',
    'Closer':           'Cerrador',
    // 日本語での入力にも対応
    '投手': 'Lanzador',   '捕手': 'Receptor',
    '一塁手': 'Primera Base', '一塁': 'Primera Base',
    '二塁手': 'Segunda Base', '二塁': 'Segunda Base',
    '三塁手': 'Tercera Base', '三塁': 'Tercera Base',
    '遊撃手': 'Campocorto',   '遊撃': 'Campocorto',
    '外野手': 'Jardinero',    '外野': 'Jardinero',
    '左翼手': 'Jardinero Izquierdo', '左翼': 'Jardinero Izquierdo',
    '中堅手': 'Jardinero Central',   '中堅': 'Jardinero Central',
    '右翼手': 'Jardinero Derecho',   '右翼': 'Jardinero Derecho',
    '指名打者': 'Bateador Designado',
    '内野手': 'Jugador de Cuadro',
};

export const translatePosition = (positionEn: string | null | undefined, locale: 'ja' | 'en' | 'es'): string | null | undefined => {
    if (!positionEn || locale === 'ja') return positionEn;

    const map = locale === 'en' ? POSITION_EN_MAP : POSITION_ES_MAP;

    // スペース区切りの複合ポジション（例: "2B 3B SS"）を各略語に分割して翻訳
    const parts = positionEn.split(' ');
    if (parts.length > 1) {
        return parts.map(p => map[p] ?? p).join(' / ');
    }

    return map[positionEn] ?? positionEn;
};

export const translateNpbPosition = (position: string | null | undefined, locale: 'ja' | 'en' | 'es'): string => {
    if (!position) return '-';
    if (locale === 'ja') return position;

    const enMap: Record<string, string> = {
        '投手': 'P', '捕手': 'C',
        '一塁手': '1B', '二塁手': '2B', '三塁手': '3B',
        '遊撃手': 'SS', '外野手': 'OF', '指名打者': 'DH',
    };
    const esMap: Record<string, string> = {
        '投手': 'P', '捕手': 'C',
        '一塁手': '1B', '二塁手': '2B', '三塁手': '3B',
        '遊撃手': 'SS', '外野手': 'JO', '指名打者': 'BD',
    };
    return (locale === 'es' ? esMap[position] : enMap[position]) ?? position;
};

export const translateNpbAwardLeague = (league: string | null | undefined, locale: 'ja' | 'en' | 'es'): string => {
    if (!league) return '-';

    const raw = league.trim();
    const upper = raw.toUpperCase();
    const key =
        upper === 'NPB' ? 'npb'
        : raw === 'セントラル' || raw === 'セ・リーグ' || upper === 'CENTRAL' ? 'central'
        : raw === 'パシフィック' || raw === 'パ・リーグ' || upper === 'PACIFIC' ? 'pacific'
        : raw === 'ファーム' || upper === 'FARM' ? 'farm'
        : '';

    const labels: Record<string, Record<'ja' | 'en' | 'es', string>> = {
        npb: {
            ja: 'プロ野球（NPB）（1リーグ）',
            en: 'Nippon Professional Baseball (NPB) (One League)',
            es: 'Béisbol Profesional Japonés (NPB) (Liga Única)',
        },
        central: {
            ja: 'セントラルリーグ',
            en: 'Central League',
            es: 'Liga Central',
        },
        pacific: {
            ja: 'パシフィックリーグ',
            en: 'Pacific League',
            es: 'Liga del Pacífico',
        },
        farm: {
            ja: 'ファーム',
            en: 'Farm',
            es: 'Farm',
        },
    };

    return key ? labels[key][locale] : raw;
};

export const translateNpbAwardDivision = (division: string | null | undefined, locale: 'ja' | 'en' | 'es'): string => {
    if (!division) return '-';
    const raw = division.trim();
    if (locale === 'ja') return raw;

    const enMap: Record<string, string> = {
        '投手': 'Pitcher',
        '打者': 'Batter',
        'イースタン': 'Eastern League',
        'ウエスタン': 'Western League',
        '東地区': 'East District',
        '中地区': 'Central District',
        '西地区': 'West District',
    };
    const esMap: Record<string, string> = {
        '投手': 'Lanzador',
        '打者': 'Bateador',
        'イースタン': 'Liga Oriental',
        'ウエスタン': 'Liga Occidental',
        '東地区': 'Distrito Este',
        '中地区': 'Distrito Central',
        '西地区': 'Distrito Oeste',
    };

    return (locale === 'es' ? esMap[raw] : enMap[raw]) ?? raw;
};

export const translateNpbAwardTeam = (teamName: string | null | undefined, locale: 'ja' | 'en' | 'es'): string => {
    if (!teamName) return '-';
    const raw = teamName.trim();
    if (!raw) return '-';
    if (locale === 'ja') return raw;

    const currentTeamParts = raw.split(/\s*現所属[:：]\s*/);
    if (currentTeamParts.length === 2) {
        const formerTeam = translateNpbAwardTeam(currentTeamParts[0], locale);
        const currentTeam = translateNpbAwardTeam(currentTeamParts[1], locale);
        return locale === 'es'
            ? `${formerTeam} (actualmente ${currentTeam})`
            : `${formerTeam} (currently ${currentTeam})`;
    }

    const map: Record<string, string> = {
        '読売': 'Yomiuri',
        '巨人': 'Giants',
        '読売ジャイアンツ': 'Yomiuri Giants',
        '阪神': 'Hanshin',
        '阪神タイガース': 'Hanshin Tigers',
        '中日': 'Chunichi',
        '中日ドラゴンズ': 'Chunichi Dragons',
        '広島': 'Hiroshima',
        '広島東洋': 'Hiroshima Toyo',
        '広島東洋カープ': 'Hiroshima Toyo Carp',
        'ヤクルト': 'Yakult',
        '東京ヤクルト': 'Tokyo Yakult',
        '東京ヤクルトスワローズ': 'Tokyo Yakult Swallows',
        '横浜': 'Yokohama',
        '横浜DeNA': 'Yokohama DeNA',
        '横浜DeNAベイスターズ': 'Yokohama DeNA BayStars',
        '湘南': 'Shonan',
        '湘南シーレックス': 'Shonan Searex',
        '福岡ソフトバンク': 'Fukuoka SoftBank',
        '福岡ソフトバンクホークス': 'Fukuoka SoftBank Hawks',
        'ソフトバンク': 'SoftBank',
        '北海道日本ハム': 'Hokkaido Nippon-Ham',
        '北海道日本ハムファイターズ': 'Hokkaido Nippon-Ham Fighters',
        '日本ハム': 'Nippon-Ham',
        '日ハム': 'Nippon-Ham',
        '千葉ロッテ': 'Chiba Lotte',
        '千葉ロッテマリーンズ': 'Chiba Lotte Marines',
        'ロッテ': 'Lotte',
        '埼玉西武': 'Saitama Seibu',
        '埼玉西武ライオンズ': 'Saitama Seibu Lions',
        '西武': 'Seibu',
        '西武ライオンズ': 'Seibu Lions',
        'インボイス': 'Invoice',
        'オリックス': 'Orix',
        'オリックス・バファローズ': 'Orix Buffaloes',
        'サーパス': 'Surpass',
        '東北楽天': 'Tohoku Rakuten',
        '東北楽天ゴールデンイーグルス': 'Tohoku Rakuten Golden Eagles',
        '楽天': 'Rakuten',
        'オイシックス新潟': 'Oisix Niigata',
        'オイシックス新潟アルビレックスBC': 'Oisix Niigata Albirex BC',
        'ハヤテ静岡': 'Hayate Shizuoka',
        'くふうハヤテ': 'Kufu Hayate',
        'くふうハヤテベンチャーズ静岡': 'Kufu Hayate Ventures Shizuoka',
        '福岡ダイエー': 'Fukuoka Daiei',
        'ダイエー': 'Daiei',
        '大阪近鉄': 'Osaka Kintetsu',
        '近鉄': 'Kintetsu',
        '阪急': 'Hankyu',
        '南海': 'Nankai',
        '大洋': 'Taiyo',
        '横浜大洋': 'Yokohama Taiyo',
    };

    return map[raw] ?? raw;
};

export const translateNationality = (nationalityEn: string | null | undefined, locale: 'ja' | 'en' | 'es'): string | null | undefined => {
    if (!nationalityEn || locale !== 'es') return nationalityEn;

    const natMap: Record<string, string> = {
        'Japan': 'Japón',
        '日本': 'Japón',
        'USA': 'EE. UU.',
        'United States': 'EE. UU.',
        'アメリカ合衆国': 'EE. UU.',
        'アメリカ': 'EE. UU.',
        'Dominican Republic': 'República Dominicana',
        'ドミニカ共和国': 'República Dominicana',
        'Venezuela': 'Venezuela',
        'ベネズエラ': 'Venezuela',
        'Cuba': 'Cuba',
        'キューバ': 'Cuba',
        'Puerto Rico': 'Puerto Rico',
        'プエルトリコ': 'Puerto Rico',
        'Mexico': 'México',
        'メキシコ': 'México',
        'Canada': 'Canadá',
        'カナダ': 'Canadá',
        'South Korea': 'Corea del Sur',
        '韓国': 'Corea del Sur',
        '大韓民国': 'Corea del Sur',
        'Taiwan': 'Taiwán',
        '台湾': 'Taiwán',
        '中華民国': 'Taiwán',
        'Colombia': 'Colombia',
        'コロンビア': 'Colombia',
        'Panama': 'Panamá',
        'パナマ': 'Panamá',
        'Nicaragua': 'Nicaragua',
        'ニカラグア': 'Nicaragua',
        'Curacao': 'Curazao',
        'キュラソー': 'Curazao',
        'Aruba': 'Aruba',
        'アルバ': 'Aruba',
        'Australia': 'Australia',
        'オーストラリア': 'Australia',
        'Netherlands': 'Países Bajos',
        'オランダ': 'Países Bajos',
        'Brazil': 'Brasil',
        'ブラジル': 'Brasil',
        'Germany': 'Alemania',
        'ドイツ': 'Alemania',
        'Italy': 'Italia',
        'イタリア': 'Italia',
        'Bahamas': 'Bahamas',
        'バハマ': 'Bahamas',
        'Honduras': 'Honduras',
        'ホンジュラス': 'Honduras',
    };
    return natMap[nationalityEn] || nationalityEn;
};
